import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, Sparkles, Plus, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { mealAPI, mealPlanAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getTodayString } from '../utils/dateUtils';

function ImportRecipe() {
  const navigate = useNavigate();
  const [recipeText, setRecipeText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingToPlan, setAddingToPlan] = useState(false);
  const [saved, setSaved] = useState(false);

  const exampleRecipes = [
    {
      title: 'Quick Chicken Salad',
      text: 'Grilled Chicken Salad\nServes 1\n\nIngredients:\n- 150g grilled chicken breast\n- 2 cups mixed greens\n- 1/4 cup cherry tomatoes\n- 1/4 cucumber, sliced\n- 2 tbsp olive oil\n- 1 tbsp balsamic vinegar\n- Salt and pepper to taste'
    },
    {
      title: 'Protein Smoothie',
      text: 'Post-Workout Smoothie\nServes 1\n\n- 1 scoop whey protein (25g)\n- 1 banana\n- 1 cup almond milk\n- 1 tbsp peanut butter\n- 1/2 cup ice'
    }
  ];

  function analyzeRecipe() {
    if (!recipeText.trim()) {
      toast.error('Please enter a recipe');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    setResult(null);
    
    mealAPI.importRecipe({ recipe_text: recipeText })
      .then(function(response) {
        setResult(response.data);
        toast.success('Recipe analyzed!');
      })
      .catch(function(err) {
        console.error('Import error:', err);
        var errorMsg = err.response?.data?.detail || 'Failed to analyze recipe';
        setError(errorMsg);
        toast.error(errorMsg);
      })
      .finally(function() {
        setAnalyzing(false);
      });
  }

  function saveToLibrary() {
    if (!result) return;
    
    setSaving(true);
    mealAPI.createCustomMeal({
      name: result.name,
      category: 'Lunch',
      calories: result.calories,
      protein: result.protein,
      carbs: result.carbs,
      fat: result.fat,
      ingredients: result.ingredients,
      cooking_instructions: [],
      cook_time_minutes: 30,
      serving_size: 1,
      allergens: [],
      is_custom: true
    })
    .then(function() {
      setSaved(true);
      toast.success('Saved to your meal library!');
    })
    .catch(function() {
      toast.error('Failed to save meal');
    })
    .finally(function() {
      setSaving(false);
    });
  }

  function addToPlan() {
    if (!result) return;
    
    setAddingToPlan(true);
    var today = getTodayString();
    
    var savePromise = saved 
      ? Promise.resolve({ data: { meal_id: 'existing' } })
      : mealAPI.createCustomMeal({
          name: result.name,
          category: 'Lunch',
          calories: result.calories,
          protein: result.protein,
          carbs: result.carbs,
          fat: result.fat,
          ingredients: result.ingredients,
          cooking_instructions: [],
          cook_time_minutes: 30,
          serving_size: 1,
          allergens: [],
          is_custom: true
        });
    
    savePromise
      .then(function(saveRes) {
        var mealId = saveRes.data.meal_id || 'custom_' + Date.now();
        if (!saved) setSaved(true);
        
        return mealPlanAPI.addMealPlan({
          date: today,
          meal_type: 'Lunch',
          meal_id: mealId,
          meal_name: result.name,
          source: 'imported'
        });
      })
      .then(function() {
        toast.success('Added to today\'s plan!');
        navigate('/');
      })
      .catch(function() {
        toast.error('Failed to add to plan');
      })
      .finally(function() {
        setAddingToPlan(false);
      });
  }

  function resetForm() {
    setResult(null);
    setRecipeText('');
    setError(null);
    setSaved(false);
  }

  function handleTextChange(e) {
    setRecipeText(e.target.value);
  }

  function handleExampleClick(text) {
    setRecipeText(text);
  }

  function goBack() {
    navigate(-1);
  }

  // Pre-render example buttons
  var exampleButtons = [];
  for (var i = 0; i < exampleRecipes.length; i++) {
    var recipe = exampleRecipes[i];
    exampleButtons.push(
      <button
        key={i}
        onClick={handleExampleClick.bind(null, recipe.text)}
        className="p-3 text-left rounded-lg border border-border/40 bg-card hover:border-accent/50 transition-colors"
        data-testid={'example-' + i}
      >
        <p className="text-sm font-medium">{recipe.title}</p>
        <p className="text-xs text-muted-foreground mt-1">Tap to use</p>
      </button>
    );
  }

  // Pre-render ingredients list
  var ingredientsList = [];
  if (result && result.ingredients) {
    for (var j = 0; j < result.ingredients.length; j++) {
      ingredientsList.push(
        <li key={j} className="text-foreground">• {result.ingredients[j]}</li>
      );
    }
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Header — header-safe-top provides single safe-area + 8px top offset */}
      <div className="header-safe-top px-4 pb-4 border-b border-border/40">
        <div className="flex items-center gap-3">
          <Button 
            onClick={goBack} 
            variant="ghost" 
            size="icon" 
            className="rounded-full h-9 w-9"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-xl">Import Recipe</h1>
            <p className="text-xs text-muted-foreground">Paste a recipe and we'll calculate the macros</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-sm font-medium">Recipe Text</span>
            <textarea
              value={recipeText}
              onChange={handleTextChange}
              placeholder="Paste your recipe here including ingredients and quantities..."
              className="mt-2 w-full h-48 p-4 rounded-xl border border-border/40 bg-card resize-none focus:outline-none focus:ring-2 focus:ring-accent/50 text-sm"
              data-testid="recipe-input"
            />
          </label>
          
          <Button
            onClick={analyzeRecipe}
            disabled={analyzing || !recipeText.trim()}
            className="w-full h-12 rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <span className="flex items-center">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Analyzing...
              </span>
            ) : (
              <span className="flex items-center">
                <Sparkles size={18} className="mr-2" />
                Analyze Recipe
              </span>
            )}
          </Button>
        </motion.div>

        {!result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-3"
          >
            <p className="text-sm text-muted-foreground">Or try an example:</p>
            <div className="grid grid-cols-2 gap-2">
              {exampleButtons}
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-destructive/10 border border-destructive/30 flex items-start gap-3"
          >
            <AlertCircle size={20} className="text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-destructive">Analysis Failed</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
            </div>
          </motion.div>
        )}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            data-testid="result-section"
          >
            <div className="p-6 rounded-xl border border-accent/30 bg-accent/5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs text-accent uppercase tracking-wide">Analyzed Recipe</p>
                  <h2 className="font-heading font-bold text-xl mt-1" data-testid="result-name">{result.name}</h2>
                </div>
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Check size={20} className="text-accent" />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="text-center p-3 rounded-lg bg-background">
                  <p className="font-heading font-bold text-lg text-accent" data-testid="result-calories">{result.calories}</p>
                  <p className="text-xs text-muted-foreground">Calories</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background">
                  <p className="font-heading font-bold text-lg" data-testid="result-protein">{result.protein}g</p>
                  <p className="text-xs text-muted-foreground">Protein</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background">
                  <p className="font-heading font-bold text-lg" data-testid="result-carbs">{result.carbs}g</p>
                  <p className="text-xs text-muted-foreground">Carbs</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background">
                  <p className="font-heading font-bold text-lg" data-testid="result-fat">{result.fat}g</p>
                  <p className="text-xs text-muted-foreground">Fat</p>
                </div>
              </div>
              
              {ingredientsList.length > 0 && (
                <div className="pt-4 border-t border-accent/20">
                  <p className="text-xs text-muted-foreground mb-2">Ingredients:</p>
                  <ul className="text-sm space-y-1">
                    {ingredientsList}
                  </ul>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={saveToLibrary}
                disabled={saving || saved}
                variant="outline"
                className="h-12 rounded-full"
                data-testid="save-library-btn"
              >
                {saved ? (
                  <span className="flex items-center">
                    <Check size={18} className="mr-2 text-accent" />
                    Saved
                  </span>
                ) : saving ? (
                  <span className="flex items-center">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Upload size={18} className="mr-2" />
                    Save to Library
                  </span>
                )}
              </Button>
              
              <Button
                onClick={addToPlan}
                disabled={addingToPlan}
                className="h-12 rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
                data-testid="add-plan-btn"
              >
                {addingToPlan ? (
                  <span className="flex items-center">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Adding...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Plus size={18} className="mr-2" />
                    Add to Today
                  </span>
                )}
              </Button>
            </div>

            <Button
              onClick={resetForm}
              variant="ghost"
              className="w-full"
              data-testid="analyze-another-btn"
            >
              Analyze Another Recipe
            </Button>
          </motion.div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default ImportRecipe;
